import { CellDoubleClickedEvent, ColDef, PostSortRowsParams } from "ag-grid-community";
import { RaonColDef } from "src/types/grid";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { CreateCellClickHandler, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, RemoveRowHandler, InsertRowHandler, postSortHandler, useCellChangeHandler, useRowSelectionHandler, createCellCssClassRules, GridCellConfig, createGridDropDownCommitAfter } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, getRowStyleFlags, IndexParameterCell, onGridReady } from "src/utils/grid/gridStyleUtils";
import { usePUR1030_R01Store } from "./Store_PUR1030_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultPUR1030_R01DetailDto, PUR1030_R01DetailDto } from "./Dto_PUR1030_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { getItemPopupList } from "src/apis/itemApi";
import { ItemDto } from "src/dtos/item/itemDto";
import { showWarning } from "src/utils/toast/toastUtil";

import { useDetailTableWithItemSearch } from "src/hooks/detail-table/useDetailTableWithItemSearch";

import {DetailBaseDto} from "src/types/detail-table";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { validateString } from "src/utils/string/stringUtil";
import { useCodeSearch } from "src/hooks/useCodeSearch";
import { CommonCodeColumn, getCommonCodeColumnOptions } from "src/utils/grid/GridColumnContent";
import { usePUR1030_R01Modal } from "./hooks/usePUR1030_R01Modal";
import { usePUR1030_R01CellChange } from "./hooks/PUR1030_R01CellChange";
import { isOrderLocked } from "src/utils/order/orderUtils";

const COLUMN_STATE_KEY = 'PUR1030_R01-detail-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<PUR1030_R01DetailDto> | null>;
	defaultPUR1030_R01DetailRow: PUR1030_R01DetailDto;
}

export const DetailTable_PUR1030_R01 = (props: Props) => {
	const { gridRef, defaultPUR1030_R01DetailRow } = props;

	const {
		editPUR1030_R01,
		PUR1030_R01DetailList, setPUR1030_R01DetailList,
		selectedPUR1030_R01DetailList, setSelectedPUR1030_R01DetailList, removeSelectedPUR1030_R01DetailList, addSelectedPUR1030_R01DetailList, initSelectedPUR1030_R01DetailList,
		deletedPUR1030_R01DetailList, setDeletedPUR1030_R01DetailList, addDeletedPUR1030_R01DetailList, removeDeletedPUR1030_R01DetailList,
		insertPUR1030_R01DetailList, updatePUR1030_R01DetailList, removePUR1030_R01DetailList,
		detailColumnState, setDetailColumnState,
		originalPUR1030_R01DetailList, setOriginalPUR1030_R01DetailList,
		detailScrollTop, setDetailScrollTop,
	} = usePUR1030_R01Store();
	const isLocked = isOrderLocked(editPUR1030_R01?.orderStatus);

	const { unitList } = useCommonCodeStore();

	const { setCodeSearchModalProps } = usePUR1030_R01Modal();

	const [clickable, setClickable] = useState(true);

	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex', // 행 식별 키
		gridData: PUR1030_R01DetailList,
		defaultGridData: defaultPUR1030_R01DetailRow,
		selectedRow: selectedPUR1030_R01DetailList,
		originalGridData: originalPUR1030_R01DetailList,
		updateRow: (index: number, data: any) => updatePUR1030_R01DetailList(index, data),
	});

	/**
	 * 컬럼 헤더명 매핑
	 * 필드명과 표시할 한글명을 매핑
	 */
	const headerNames: Record<string, string> = {
		rowIndex: "",           // 순번 (빈 문자열)
		itemCd: '품목코드',      // 품목 코드
		itemNm: '품명',         // 품명
		itemSpec: '규격',       // 규격
		locationNm: '보관위치',
		modelNo: '품번',        // 품번
		orderQty: '수량',       // 주문 수량
		orderUnit: '단위',      // 단위
		orderPrice: '단가',     // 단가
		dcAmt: '할인액',        // 할인액
		itemAmt: '금액',        // 금액
	}

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['itemNm', 'itemSpec', 'locationNm', 'modelNo', 'orderUnit', 'itemAmt', 'completeTransport'],
		insertedDisabledCells: [''],
		commonDisabledCells: ['itemCd', 'orderQty', 'orderPrice', 'dcAmt'],
		pointerCells: ['cancelQty', 'remark'],
		insertedPointerCells: ['itemCd', 'itemNm', 'itemSpec', 'orderUnit', 'orderQty', 'orderPrice', 'dcAmt'],
		commonPointerCells: [''],
		alignLeftCells: ['']
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<PUR1030_R01DetailDto>({
			idKey: 'valueIndex',
			selectedList: selectedPUR1030_R01DetailList,
			deletedList: deletedPUR1030_R01DetailList,
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns,
			alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules, }};
	}, [
		selectedPUR1030_R01DetailList, deletedPUR1030_R01DetailList,
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
		createCellClassRules,
	]);

	const { searchExactItemCode } = useCodeSearch({});

	/**
	 * 편집 가능한 컬럼 목록 정의
	 * 사용자가 직접 입력/수정할 수 있는 필드들을 지정
	 */
	const defaultEditableColumns = ['itemCd', 'itemNm', 'itemSpec', 'modelNo', 'orderUnit', 'orderQty', 'cancelQty', 'orderPrice', 'dcAmt', 'remark'];

	const detailTableHandlers = usePUR1030_R01CellChange({
		PUR1030_R01DetailList,
		originalPUR1030_R01DetailList,
		updatePUR1030_R01DetailList,
		gridRef,
		setCodeSearchModalProps,
	});

	const insertRowHandler = useCallback((rowIndex: number, e: React.MouseEvent) => {
			// 일반 클릭 시 실행할 로직 (기존 동작)
			InsertRowHandler({
				event: e,
				dataList: PUR1030_R01DetailList,
				rowData: PUR1030_R01DetailList[rowIndex],
				insertRow: insertPUR1030_R01DetailList,
				defaultRowData: defaultPUR1030_R01DetailRow,
				setClickable
			});
	}, [PUR1030_R01DetailList, insertPUR1030_R01DetailList, defaultPUR1030_R01DetailRow, setClickable]);

	const handleGridDropDownCommitAfter = useMemo(() => 
		createGridDropDownCommitAfter<PUR1030_R01DetailDto>(defaultEditableColumns),
	[defaultEditableColumns],);

	const baseColumnDefs: RaonColDef<PUR1030_R01DetailDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			editPUR1030_R01?.purchaseOrderNo === '' ?
				(params: PUR1030_R01DetailDto) => renderIndexCell(
					params,
					(innerParams: PUR1030_R01DetailDto) => getRowStyleFlags(
						innerParams,
						{
							deletedList: deletedPUR1030_R01DetailList,
							selectedList: selectedPUR1030_R01DetailList,
							dataList: PUR1030_R01DetailList
						}),
					(rowIndex: number, e: React.MouseEvent) => {
						if (e.ctrlKey) {
							for (let i = 0; i < 10; i++) { insertRowHandler(rowIndex, e); }
						} else { 
							insertRowHandler(rowIndex, e);
						}
					},
					(rowData) => RemoveRowHandler(
						gridRef,
						PUR1030_R01DetailList,
						rowData,  // rowData는 renderIndexCell에서 Dto 형식으로 선언함
						setClickable,
						removePUR1030_R01DetailList,
						(valueIndex) => addDeletedPUR1030_R01DetailList([valueIndex.toString()])
					),
				)
			: undefined
		),
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 77,
			editable: !isLocked && ((params) => params.data?.inserted === true),
			pasteable: true,
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 161,
			editable: false,
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 127,
			editable: false,
		},
		{
			headerName: '보관위치', colId: 'locationNm', field: 'locationNm', width: 120,
			editable: false,
		} as RaonColDef<PUR1030_R01DetailDto>,
		{
			headerName: '품번', colId: 'modelNo', field: 'modelNo', width: 130,
			editable: false,
		},
		{
			headerName: '수량', colId: 'orderQty', field: 'orderQty', width: 60,
			editable: !isLocked && ((params) => params.data?.inserted === true),
			 // editable: !isTransported ? ((params) => PUR1030_R01DetailList[params.node.rowIndex!] !== undefined) : false,
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
			valueFormatter: (params) => params.value?.toLocaleString() ?? ''
		},
		{
			headerName: '취소수량', colId: 'cancelQty', field: 'cancelQty', width: 80,
			editable: true,
			pasteable: true,  // editable:true지만 suppressClipboardPaste로 막혔으므로 명시
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
			valueFormatter: (params) => params.value?.toLocaleString() ?? ''
		},
		{
			headerName: '출하완료수량', colId: 'completeTransport', field: 'completeTransport', width: 100,
			editable: false,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? ''
		},
		{
			headerName: '단위', colId: 'orderUnit', field: 'orderUnit', width: 60,
			...CommonCodeColumn.options(unitList, !isLocked, CommonCodeColumn.change(updatePUR1030_R01DetailList), handleGridDropDownCommitAfter),
			editable: false,
		},
		{
			headerName: '단가', colId: 'orderPrice', field: 'orderPrice', width: 65,
			editable: !isLocked && ((params) => params.data?.inserted === true),
			// editable: !isTransported ? ((params) => PUR1030_R01DetailList[params.node.rowIndex!] !== undefined) : false,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '할인액', colId: 'dcAmt', field: 'dcAmt', width: 65,
			editable: !isLocked && ((params) => params.data?.inserted === true),
			// editable: !isTransported ? ((params) => PUR1030_R01DetailList[params.node.rowIndex!] !== undefined) : false,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '금액', colId: 'itemAmt', field: 'itemAmt', width: 96,
			editable:  false,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? ''
		},
		{
			headerName: '비고', colId: 'remark', field: 'remark', width: 77,
			editable: true,
			pasteable: true,  // editable:true지만 suppressClipboardPaste로 막혔으므로 명시
		},
		// ...IndexParameterCell(),
	], [deletedPUR1030_R01DetailList, PUR1030_R01DetailList, isLocked]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<PUR1030_R01DetailDto>[] = useMemo(() => {
		if (detailColumnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<PUR1030_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [detailColumnState, settingRowIndex, baseColumnDefs, headerNames]);


	const handleRowClick = useRowSelectionHandler({
		dataList: PUR1030_R01DetailList,
		idKey: 'valueIndex',
		selectedList: selectedPUR1030_R01DetailList,
		deletedList: deletedPUR1030_R01DetailList,
		addSelected: addSelectedPUR1030_R01DetailList,
		removeSelected: removeSelectedPUR1030_R01DetailList,
		clickable, setClickable
	});

	/**
	 * 품목 검색 기능이 포함된 상세 테이블 이벤트 핸들러 훅
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 * @param itemSearchConfig - 품목 검색 설정
	 * @param constraints - 제약 조건 (notTransport 등)
	 * @param keyDownConfig - 키다운 네비게이션 설정
	 */
	const cellEventHandlers = useDetailTableWithItemSearch<PUR1030_R01DetailDto>({
		detailList: PUR1030_R01DetailList,
		editableColumns: defaultEditableColumns,
		itemSearchConfig: {
			config: {
				category: '품목', // 검색 카테고리
				responseMapping: {
					// 검색 결과 → 테이블 필드 매핑
					itemCd: 'itemCd',      // 품목코드
					itemNm: 'itemNm',      // 품명
					itemSpec: 'itemSpec',  // 규격
					modelNo: 'modelNo',    // 품번
					itemUnit: 'orderUnit', // 단위
					itemCost: 'orderPrice', // 단가
					locationCd: 'locationCd',
					locationNm: 'locationNm',
				},
				clearFields: {
					// 품목 검색 시 초기화할 필드들
					itemCd: "", itemNm: "", itemSpec: "", modelNo: "",
					orderUnit: "", orderPrice: 0, dcAmt: 0, itemAmt: 0,
					locationCd: "", locationNm: "",
				},
				searchFunction: searchExactItemCode // 품목 검색 함수
			},
			settingCodeSearchModalProps: detailTableHandlers.settingCodeSearchModalProps, // 모달 설정 함수
		},
		constraints: {
			notEdit: (params) => {
				if(isLocked) return true;
				if(params.data?.inserted === true) return true;
				return false;
			}
		},
		keyDownConfig: {
			qtyField: 'orderQty',        // 엔터 키 시 수량 필드로 이동
			enableArrowNavigation: true  // 방향키 네비게이션 활성화
		}
	});


	/**
	 * 행 드래그 상태를 추적하는 ref
	 * 드래그 중일 때는 자동 정렬을 방지하여 드래그 효과를 보여줌
	 */
	const isRowDraggingRef = useRef(false);

	const handlePostSort = useCallback((params: PostSortRowsParams<PUR1030_R01DetailDto>) => {
		// 드래그 중에는 postSortRows에 의한 강제 정렬을 막아 드래그가 눈에 보이도록 함
		if (isRowDraggingRef.current) return;
		postSortHandler(params, PUR1030_R01DetailList, originalPUR1030_R01DetailList, setPUR1030_R01DetailList);
	}, [PUR1030_R01DetailList, originalPUR1030_R01DetailList, setPUR1030_R01DetailList]);

	/**
	 * 하단 고정 행 데이터 생성 (합계 행)
	 *
	 * 기능:
	 * - 수량과 금액의 총합을 계산
	 * - 하단에 고정된 합계 행으로 표시
	 * - 데이터가 없으면 undefined 반환
	 *
	 * @returns 합계 행 데이터 또는 undefined
	 */
	const pinnedBottomRowData = useMemo(() => {
		// 삭제된 행은 제외하고 수량 합계 계산
		const totalQty : number = PUR1030_R01DetailList
			.reduce((sum, item) => sum + (deletedPUR1030_R01DetailList.includes(item.itemCd) ? 0 : Number(item.orderQty) || 0), 0);

		// 삭제된 행은 제외하고 금액 합계 계산
		const totalAmt : number = PUR1030_R01DetailList
			.reduce((sum, item) => sum + (deletedPUR1030_R01DetailList.includes(item.itemCd) ? 0 : Number(item.itemAmt) || 0), 0);

		// 하단 고정 행 데이터 반환
		return [{
			itemCd: PUR1030_R01DetailList.length.toLocaleString() + "건", // 건수 표시
			orderQty: totalQty,  // 총 수량
			itemAmt: totalAmt    // 총 금액
		}];
	}, [PUR1030_R01DetailList, deletedPUR1030_R01DetailList]);

	return (
		<GridContextWrapper<PUR1030_R01DetailDto>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={PUR1030_R01DetailList}
			setPaddedGridData={setPUR1030_R01DetailList}
			initSelected={initSelectedPUR1030_R01DetailList}
			defaultRowData={defaultPUR1030_R01DetailRow}
			baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setDetailColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Excel<PUR1030_R01DetailDto>
				ref={gridRef}
				rowData={PUR1030_R01DetailList}
				getRowId={getRowId} // +- 버튼 테이블 getRowId는 valueIndex로 고정
				columnDefs={columnDefs}
				defaultColDef={cellCssDefaultColDef}

				disableBuiltinCellSelection={false}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setDetailColumnState}
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={detailScrollTop}
				setScrollTop={setDetailScrollTop}

				rowClassRules={createRowClassRules<PUR1030_R01DetailDto>({
					idKey: 'valueIndex',
					selectedList: selectedPUR1030_R01DetailList,
					deletedList: deletedPUR1030_R01DetailList
				})}

				// 이벤트 핸들러들
				onRowClicked={handleRowClick}
				onCellClicked={cellEventHandlers.handleCellClick}
				onCellDoubleClicked={cellEventHandlers.handleCellDoubleClick}
                onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				onCellValueChanged={detailTableHandlers.handleCellChange}
				onCellEditingStopped={detailTableHandlers.handleCellEditingStopped}

				// 하단 고정 행 (합계)
				pinnedBottomRowData={pinnedBottomRowData}

				// 행 드래그 설정
                rowDragManaged={true}      // 드래그 관리 활성화
                rowDragEntireRow={true}    // 전체 행 드래그
				// 드래그 이벤트 핸들러들
				onRowDragEnter={() => {
					// 드래그 시작 시 플래그 설정
					isRowDraggingRef.current = true;
				}}
                onRowDragEnd={(e: any) => {
					/**
					 * 행 드래그 종료 처리
					 *
					 * 기능:
					 * - 정렬 해제하여 사용자가 정한 순서를 유지
					 * - 드래그된 순서대로 orderIndex 재설정
					 * - 데이터 목록 업데이트
					 */
                    const api = e.api;

                    // 정렬 해제하여 사용자가 정한 순서를 유지
                    if (api.applyColumnState) {
                        api.applyColumnState({ defaultState: { sort: null as any, sortIndex: null as any } });
                    }

                    // 드래그된 순서대로 데이터 재정렬
                    const reordered: PUR1030_R01DetailDto[] = [];
                    api.forEachNodeAfterFilterAndSort((node: any, idx: number) => {
                        if (!node.data) return;
                        reordered.push({
                            ...node.data,
                            orderIndex: idx + 1,    // 순서 인덱스 재설정
                            orderIndexSeq: 0,       // 시퀀스 초기화
                        });
                    });

                    // 데이터 목록 업데이트
                    setPUR1030_R01DetailList(reordered);
					isRowDraggingRef.current = false; // 드래그 플래그 해제
                }}


				// 정렬 후 처리 (드래그 중에는 자동 정렬 방지)
				onPostSortRows={handlePostSort}
			/>
		</GridContextWrapper>
	)
}
